import { useEffect, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Code,
  Container,
  Divider,
  Heading,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AxiosError, AxiosResponse } from 'axios'
import useApi from '../../common/utils/api/useApi'
import { JoinRequestResponseDto, OrgMembershipApiClient } from './OrgMembershipSlice'
import { routes } from '../../pages/routes'
import { useAppSelector } from '../../app/typedReduxHooks'
import { Permission } from '../../types/permission'
import { MembershipEventStatus } from './type/MemberShipEventStatus'

const getRoleDisplayName = (role: 'prescriber' | 'admin', permissions: Permission[]): string => {
  if (role === 'prescriber') {
    return 'Prescriber'
  }

  if (permissions?.includes(Permission.PRESCRIPTION_MANAGEMENT)) {
    return 'Rx Admin'
  }
  return 'Patient Admin'
}

const getStatusColor = (status: string): string => {
  switch (status) {
    case MembershipEventStatus.ACCEPTED:
      return 'green'
    case MembershipEventStatus.OWNER_REJECTED:
      return 'red'
    case MembershipEventStatus.PENDING_OWNER_APPROVAL:
      return 'yellow'
    case MembershipEventStatus.PENDING:
      return 'orange'
    case MembershipEventStatus.EXPIRED:
      return 'red'
    case MembershipEventStatus.REVOKED:
      return 'red'
    default:
      return 'gray'
  }
}

const getStatusDisplayText = (status: string): string => {
  switch (status) {
    case MembershipEventStatus.ACCEPTED:
      return 'Accepted'
    case MembershipEventStatus.OWNER_REJECTED:
      return 'Rejected'
    case MembershipEventStatus.PENDING_OWNER_APPROVAL:
      return 'Pending Approval'
    case MembershipEventStatus.PENDING:
      return 'Pending'
    case MembershipEventStatus.EXPIRED:
      return 'Expired'
    case MembershipEventStatus.REVOKED:
      return 'Revoked'
    default:
      return status.charAt(0).toUpperCase() + status.slice(1)
  }
}

export const ApproveJoinRequestPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [joinRequest, setJoinRequest] = useState<JoinRequestResponseDto | null>(null)
  const authInfo = useAppSelector((state) => state.authentication)
  const adminProfile = useAppSelector((state) => state.organizationAdmin.profile)
  const organization = useAppSelector((state) => state.organization.data)

  const requestToken = searchParams.get('request_token')

  const { trigger: getJoinRequestByToken, loading: getJoinRequestLoading } = useApi(
    OrgMembershipApiClient.getJoinRequestByToken,
    {
      onSuccess: (data: JoinRequestResponseDto) => {
        setJoinRequest(data)
      },
      onFailure: (error: AxiosError) => {
        const response = error.response as AxiosResponse<{ message: string }> | undefined
        toast({
          title: 'Error',
          description: response?.data?.message
            ? response.data.message
            : 'Failed to fetch join request details',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
    }
  )

  const { trigger: approveJoinRequest, loading: approveJoinRequestLoading } = useApi(
    OrgMembershipApiClient.approveJoinRequest,
    {
      onSuccess: (resp) => {
        toast({
          title: 'Success',
          description: resp.isApproved
            ? 'Join request has been approved'
            : 'Join request has been rejected',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        })
        navigate(routes.settings.path)
      },
      onFailure: (error: AxiosError) => {
        const response = error.response as AxiosResponse<{ message: string }> | undefined
        toast({
          title: 'Error',
          description: response?.data?.message
            ? response.data.message
            : 'Failed to approve/reject join request',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        })
      },
    }
  )

  useEffect(() => {
    if (!requestToken) return
    getJoinRequestByToken({ token: requestToken })
  }, [requestToken])

  if (authInfo.loginFinished) {
    if (!adminProfile?.isOwner) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="flex-start"
          paddingTop="10vh"
          minHeight="100vh"
          width="100%"
        >
          <Container
            maxW="container.sm"
            py={10}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <VStack spacing={8} width="100%">
              <Heading color="red.500">Permission Denied</Heading>
              <Box w="100%" p={6} borderWidth="1px" borderRadius="lg" bg="red.50">
                <VStack align="stretch" spacing={4}>
                  <Text fontSize="lg" fontWeight="bold">
                    You don't have permission to approve join requests
                  </Text>
                  <Text>
                    Only organisation owners can approve or reject join requests. Please contact
                    your organisation owner for assistance.
                  </Text>
                  <Button colorScheme="blue" onClick={() => navigate(routes.root.path)}>
                    Return to Home
                  </Button>
                </VStack>
              </Box>
            </VStack>
          </Container>
        </Box>
      )
    }

    if (joinRequest && joinRequest.organizationId !== organization?.id) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="flex-start"
          paddingTop="10vh"
          minHeight="100vh"
          width="100%"
        >
          <Container
            maxW="container.sm"
            py={10}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <VStack spacing={8} width="100%">
              <Heading color="red.500">Organisation Mismatch</Heading>
              <Box w="100%" p={6} borderWidth="1px" borderRadius="lg" bg="red.50">
                <VStack align="stretch" spacing={4}>
                  <Text fontSize="lg" fontWeight="bold">
                    This join request belongs to a different organisation
                  </Text>
                  <Text>
                    You are logged in as an owner of {organization?.name}, but this join request is
                    for organisation ID: {joinRequest.organizationId}.
                  </Text>
                  <Button colorScheme="blue" onClick={() => navigate(routes.root.path)}>
                    Return to Home
                  </Button>
                </VStack>
              </Box>
            </VStack>
          </Container>
        </Box>
      )
    }
  }

  if (!requestToken) {
    return (
      <Container centerContent py={10}>
        <Text>No request token provided</Text>
      </Container>
    )
  }

  if (getJoinRequestLoading) {
    return (
      <Container centerContent py={10}>
        <Spinner size="xl" />
      </Container>
    )
  }

  if (!joinRequest) {
    return (
      <Container centerContent py={10}>
        <Text>Join request not found</Text>
      </Container>
    )
  }

  const handleApprove = async () => {
    await approveJoinRequest({
      requestToken,
      isApproved: true,
    })
  }

  const handleReject = async () => {
    await approveJoinRequest({
      requestToken,
      isApproved: false,
    })
  }

  const isPending = joinRequest.status === MembershipEventStatus.PENDING_OWNER_APPROVAL
  const isApproved = joinRequest.status === MembershipEventStatus.ACCEPTED
  const isRejected = joinRequest.status === MembershipEventStatus.OWNER_REJECTED

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="flex-start"
      paddingTop="10vh"
      minHeight="100vh"
      width="100%"
    >
      <Container
        maxW="container.sm"
        py={10}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <VStack spacing={8} width="100%">
          <Heading>Approve Join Request</Heading>

          <Box w="100%" p={6} borderWidth="1px" borderRadius="lg">
            <VStack align="stretch" spacing={4}>
              <Text>
                Token: <Code>{joinRequest.token}</Code>
              </Text>
              <Divider />

              <Text fontSize="lg" fontWeight="bold">
                Join Request Details:
              </Text>
              <Text>Email: {joinRequest.email}</Text>
              {(joinRequest.firstName || joinRequest.lastName) && (
                <Text>
                  Name: {[joinRequest.firstName, joinRequest.lastName].filter(Boolean).join(' ')}
                </Text>
              )}
              <Text>
                Requested Role:{' '}
                {getRoleDisplayName(joinRequest.requestedRole, joinRequest.requestedPermissions)}
              </Text>
              <Text>Organisation ID: {joinRequest.organizationId}</Text>
              <Text>
                Created: {new Date(joinRequest.createdAt).toLocaleDateString()}{' '}
                {new Date(joinRequest.createdAt).toLocaleTimeString()}
              </Text>
              <Text>
                Expires: {new Date(joinRequest.expiresAt).toLocaleDateString()}{' '}
                {new Date(joinRequest.expiresAt).toLocaleTimeString()}
              </Text>
              <Text>
                Status:
                <Badge ml={2} colorScheme={getStatusColor(joinRequest.status)}>
                  {getStatusDisplayText(joinRequest.status)}
                </Badge>
              </Text>

              {joinRequest.organizationId === organization?.id ? (
                isPending ? (
                  <Box display="flex" gap={4} mt={4}>
                    <Button
                      colorScheme="blue"
                      size="lg"
                      flex="1"
                      onClick={handleApprove}
                      isLoading={approveJoinRequestLoading}
                      disabled={approveJoinRequestLoading}
                    >
                      Approve
                    </Button>
                    <Button
                      colorScheme="red"
                      size="lg"
                      flex="1"
                      onClick={handleReject}
                      isLoading={approveJoinRequestLoading}
                      disabled={approveJoinRequestLoading}
                    >
                      Reject
                    </Button>
                  </Box>
                ) : isRejected ? (
                  <Badge
                    variant="solid"
                    colorScheme="red"
                    alignSelf="center"
                    width="50%"
                    py={2}
                    textAlign="center"
                    rounded="md"
                    fontSize="lg"
                  >
                    Rejected
                  </Badge>
                ) : isApproved ? (
                  <Badge
                    variant="solid"
                    colorScheme="green"
                    alignSelf="center"
                    width="50%"
                    py={2}
                    textAlign="center"
                    rounded="md"
                    fontSize="lg"
                  >
                    Approved
                  </Badge>
                ) : null
              ) : null}
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  )
}
